/**
 * PendingWorkCard — renders a PendingWorkSummary tool result.
 * de-CH labels; Badge variants for urgency.
 * [Source: Story P1b spec §8]
 */
import type { PendingWorkSummary } from '@brokly/shared';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';

interface Props {
  data: PendingWorkSummary;
}

export function PendingWorkCard({ data }: Props) {
  return (
    <Card className="p-3 text-sm">
      {/* Offene Aufgaben */}
      <div className="mb-2 flex items-center justify-between">
        <span className="font-medium text-foreground">Offene Aufgaben</span>
        <Badge variant="neutral">{data.tasks.count}</Badge>
      </div>
      {data.tasks.items.length > 0 && (
        <ul className="mb-3 space-y-0.5 text-xs text-muted-foreground">
          {data.tasks.items.slice(0, 3).map((t) => (
            <li key={t.id} className="truncate">
              {t.title}
            </li>
          ))}
        </ul>
      )}

      {/* Dringende Renewals */}
      <div className="mb-1 flex items-center gap-2">
        <span className="font-medium text-foreground">Dringende Renewals</span>
        {data.renewals.red > 0 && (
          <Badge variant="alert">{data.renewals.red} rot</Badge>
        )}
        {data.renewals.orange > 0 && (
          <Badge variant="decide">{data.renewals.orange} orange</Badge>
        )}
      </div>
      {data.renewals.items.length > 0 && (
        <ul className="mb-3 space-y-0.5 text-xs text-muted-foreground">
          {data.renewals.items.slice(0, 3).map((r) => (
            <li key={r.policy_id} className="truncate">
              <span className="mono-num">{r.policy_number ?? r.policy_id}</span> · {r.bucket}
            </li>
          ))}
        </ul>
      )}

      {/* Rechnungen in Prüfung */}
      <div className="flex items-center justify-between">
        <span className="font-medium text-foreground">Rechnungen in Prüfung</span>
        <Badge variant="neutral">{data.invoices.awaitingCount}</Badge>
      </div>
    </Card>
  );
}
